import * as React from 'react';

import styled, { css } from 'styled-components';

import { AnimatePresence } from 'framer-motion';

import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import PlusIcon from '@components/basics/icons/plus';
import Typography from '@components/basics/typography';
import MinusIcon from '@components/basics/icons/minus';
import MotionOpacity from '@components/motion/motion-opacity';
import UnselectedIcon from '@components/basics/icons/unselected';

export interface CheckboxProps {
  className?: string;
  hasIcons?: React.ReactNode;
  title: React.ReactNode;
  text?: React.ReactNode;
  active: boolean;
  titleDecoration?: React.ReactNode;
  textDecoration?: React.ReactNode;
  disabled?: boolean;
  big?: boolean;
  iconDecoration?: React.ReactNode;
  customBackground?: string;
  customContent?: React.ReactNode;
  customBorderRadius?: string;
  onClick?: () => void;
}

export const Checkbox: React.FC<CheckboxProps> = (props) => {
  return (
    <Box
      className={props.className}
      onClick={() =>
        props.onClick && !props.disabled ? props.onClick() : null
      }
    >
      <Flex alignItems="center" justifyContent="space-between" gap={20}>
        <Flex className="info" alignItems="center" justifyContent="flex-start">
          {props.iconDecoration ? (
            <div className="icon-decoration">{props.iconDecoration}</div>
          ) : null}
          <InfoWrapper>
            <Flex className="title-wrapper" alignItems="center">
              <Typography className="title" weight="700">
                {props.title}
              </Typography>
              {props.titleDecoration ? props.titleDecoration : null}
            </Flex>
            {props.text ? (
              <Typography className="text" type="small">
                {props.text}
              </Typography>
            ) : null}
            {props.customContent && (
              <DecorationWrapper>{props.customContent}</DecorationWrapper>
            )}
          </InfoWrapper>
        </Flex>
        <Flex alignItems="center" gap={10}>
          {!props.disabled && props.hasIcons ? props.hasIcons : null}
          {props.textDecoration ? (
            <Typography uppercase type="small" weight="700">
              {props.textDecoration}
            </Typography>
          ) : null}
          {props.disabled ? (
            <UnselectedIcon className="icon disabled" noLine noAnimation />
          ) : props.active ? (
            <CircleIcon isRed>
              <MinusIcon className="icon red" color="accent" />
            </CircleIcon>
          ) : (
            <CircleIcon>
              <PlusIcon isWhite />
            </CircleIcon>
          )}
        </Flex>
      </Flex>
      {props.children ? (
        <AnimatePresence>
          <MotionOpacity>{props.children}</MotionOpacity>
        </AnimatePresence>
      ) : null}
    </Box>
  );
};

export default styled(Checkbox)`
  padding: 20px;
  background-color: ${(props) =>
    props.customBackground ? props.customBackground : 'transparent'};
  transition: all
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};
  cursor: ${(props) => (!props.disabled ? 'pointer' : 'auto')};
  position: relative;
  border-radius: ${(props) =>
    props.customBorderRadius ? props.customBorderRadius : '4px'};
  border: 1px dashed ${({ theme }) => theme.colors.border};

  ${(props) =>
    props.big
      ? css`
          @media ${({ theme }) => theme.device.tablet} {
            padding: 30px;
          }
        `
      : null}

  .info {
    width: 100%;
  }

  .title-wrapper {
    ${(props) =>
      props.text
        ? css`
            margin-bottom: 5px;
          `
        : null};
  }

  .text {
    color: ${({ theme }) => theme.colors.tertiary};
  }

  .icon-decoration {
    max-height: 24px;
    max-width: 24px;
  }

  .icon {
    &.disabled {
      path {
        stroke: ${({ theme }) => theme.colors.border};
      }
    }
  }

  ${(props) =>
    props.active && !props.disabled
      ? css`
          background-color: ${({ theme }) => theme.colors.differentBackground};
          border-color: ${({ theme }) => theme.colors.differentBackground};
        `
      : null}

  ${(props) =>
    props.disabled
      ? css`
          .title,
          .text {
            color: ${({ theme }) => theme.colors.border};
          }
        `
      : null}
`;

const InfoWrapper = styled.div`
  width: 100%;
`;

const CircleIcon = styled.div<{ isRed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  border-radius: 30px;
  width: 32px;
  height: 32px;
  background-color: ${({ theme, isRed }) =>
    isRed ? theme.colors.accentLight : theme.colors.blueColor};
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    background-color: ${({ theme, isRed }) =>
      isRed ? theme.colors.accentHover : theme.colors.blueColorDark};

    .icon {
      &.red {
        path {
          fill: ${({ theme }) => theme.colors.white};
        }
      }
    }
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const DecorationWrapper = styled.div`
  margin-top: 20px;
`;
